// polyfill for Uint8Array.toBase64
import { ApolloError, ApolloProvider, ServerError } from '@apollo/client';
import { CacheProvider } from '@emotion/react';
import { StyledEngineProvider, ThemeProvider } from '@mui/material/styles';
import * as Sentry from '@sentry/react';
import { excludeGraphQLFetch } from 'apollo-link-sentry';
import 'core-js/modules/esnext.uint8-array.to-base64.js';
import moment from 'moment-timezone';
import React from 'react';
import { createRoot } from 'react-dom/client';
import { Provider as ReduxProvider } from 'react-redux';
import {
  RouterProvider,
  createBrowserRouter,
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router';

import App from '@/App';
import ActionTypes from '@/actions/ActionTypes';
import { createToken, updateCall } from '@/actions/call';
import { track } from '@/actions/tracking';
import { notify, setUserContext } from '@/actions/ui';
import { TokenMeta, logout } from '@/auth';
import { setViewer } from '@/auth/store/viewerSlice';
import { ErrorFallback } from '@/components/ErrorFallback';
// import PlivoCarrier from '@/conference/plivoCarrier';
// import TwilioCarrier from '@/conference/twilioCarrier';
import voipCarrier from '@/conference/voipCarrier';
import ZoomCarrier from '@/conference/zoomCarrier';
import config from '@/config';
import { EPA_GATE_KILL_SWITCH } from '@/core/agreements';
import { initSegmentIdentifier } from '@/core/analytics';
import {
  GraphQLClient,
  UnauthorizedError,
  bearerAuth,
  isApolloSessionExpiry,
  isSessionExpiryCode,
} from '@/core/api';
import ApiWebSocket from '@/core/apiWebSocket';
import '@/core/compat';
import { initHubSpot, initIntercom } from '@/core/crm';
import { installTranslationDomGuard } from '@/core/domTranslationGuard';
import { growthBookReady, isFeatureOn, loadGrowthBookFeatures } from '@/core/growthbook';
import { saveInvitationToken } from '@/core/invite';
import { PermissionService, registerPermissionService } from '@/core/permissions';
import { sentryDenyUrls } from '@/core/sentryDenyUrls';
import { sentryIgnoreErrors } from '@/core/sentryIgnoreErrors';
import { updateTracking } from '@/core/tracking';
import { getDefaultUserContext } from '@/core/user';
import { Viewer, fetchViewer } from '@/core/viewer';
import { AppProvider } from '@/hooks/useAppContext';
import { useThemeMode } from '@/hooks/useThemeMode';
import SendbirdHelper from '@/messaging/sendbird';
import { updateProfile } from '@/profile/store';
import store, { AppStore, setThunkExtraArg } from '@/store';
import { setRuntimeVariable } from '@/store/runtime';
import { updateUserInfo } from '@/store/user';
import theme, { createEmotionCache, lightTheme } from '@/theme';
import { ThemeModeProvider } from '@/theme/ThemeProvider';
import '@/theme/index.css';
import { getCache, rateDisplay } from '@/utils';

import './overrides';
import reportWebVitals from './reportWebVitals';
import createRoutes from './routes';

declare global {
  // Polyfills
  interface Uint8Array {
    toBase64(): string;
  }

  interface Window {
    __GIT_SHA__?: string;
    OF_SEARCH_V2_FEATURE?: string;
    OF_KNOWLEDGE_TABLE_FEATURE?: string;
    dataLayer: (string | object)[];
    _hsq?: (string | object)[];
    _analyticsTraits?: object;
    analytics: SegmentAnalytics.AnalyticsJS;
    growthbookFeaturesOnUsage: Record<string, boolean | number | string>;
    HubSpotConversations?: {
      widget?: {
        status: () => { loaded: boolean };
        open: () => void;
      };
    };
  }
}

interface VoipError {
  message: string;
}

// The build's commit is its identity: the Sentry release, and what the release
// tooling compares against (ENG-5845). package.json's version has not moved
// since 2025, so it told builds apart from nothing.
const gitSha = import.meta.env.VITE_GIT_SHA;
window.__GIT_SHA__ = gitSha;

function trackConsultationJoin(store: AppStore, action: string) {
  const { call } = store.getState();
  store.dispatch(
    track(
      `consultation.join.web.${action}`,
      call.consultationId,
      {
        ua: navigator && navigator.userAgent,
      },
      false
    )
  );
}

function saveTimezone(store: AppStore, viewer: Viewer) {
  if (!viewer.id || !viewer.profile || viewer.timezone) return;
  // Boot-time and unprompted, so a failure is reported but never shown. The next
  // boot retries, because viewer.timezone is still unset.
  store
    .dispatch(
      updateProfile({
        id: viewer.profile.id,
        timezone: moment.tz.guess(),
      })
    )
    .catch((err) => Sentry.captureException(err));
}

function MuiThemeBridge({ children }: { children: React.ReactNode }) {
  const { resolvedTheme } = useThemeMode();
  const muiTheme = resolvedTheme === 'light' ? lightTheme : theme;
  return <ThemeProvider theme={muiTheme}>{children}</ThemeProvider>;
}

function isUnauthorizedError(err: unknown): boolean {
  if (!(err instanceof ApolloError)) return false;
  return (err?.networkError as ServerError)?.statusCode === 401;
}

(async function main() {
  // Fired before anything awaits, so the flag fetch overlaps the viewer round
  // trip instead of adding to it. Awaited just before the router is built, at
  // the bottom of this function. ENG-5601.
  loadGrowthBookFeatures();

  // Make React's DOM commits tolerant of browser page-translation, which moves
  // React-managed nodes and otherwise crashes the tree (Sentry WEBAPP-CC7 and
  // its siblings). Must run before the first render below.
  installTranslationDomGuard();

  Sentry.init({
    release: gitSha,
    // QA and prod build the same commit with different settings, so the
    // environment tells their source maps apart (see vite.config.ts).
    dist: config.appEnv,
    environment: config.appEnv,
    dsn: config.sentryDsn,
    integrations: [
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect: React.useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.replayIntegration({
        unblock: ['.sentry-unblock, [data-sentry-unblock]'],
        unmask: ['.sentry-unmask, [data-sentry-unmask]'],
      }),
      Sentry.captureConsoleIntegration({ levels: ['error'] }),
    ],

    ignoreErrors: sentryIgnoreErrors,

    denyUrls: sentryDenyUrls,

    beforeSend(event: Sentry.ErrorEvent, hint: Sentry.EventHint) {
      // Drop only events that carry neither an exception nor a message. captureMessage
      // events reach beforeSend too — Sentry classifies every event without a `type` as an
      // error event — and with `attachStacktrace` unset they never gain an `exception`, so
      // testing `!event.exception` alone discarded all of them client-side. That silently
      // swallowed the warning ENG-5628 leans on as its only remaining signal once the
      // throw is gone, and ENG-5528's before it.
      if (!event.exception && !event.message) {
        return null;
      }
      // Mid-session JWT expiry: handleError in src/core/api.ts redirects the
      // user to /login, but the throw races the navigation and Sentry's
      // global handlers can still fire on the unhandled rejection (Sentry
      // WEBAPP-BW1) or the apollo-link-sentry capture of the underlying
      // ApolloError 401 (Sentry WEBAPP-BW2). Both originate from a session
      // boundary the user is recovering from — not an application bug.
      const orig = hint?.originalException;
      if (orig instanceof UnauthorizedError && isSessionExpiryCode(orig.code)) {
        return null;
      }
      if (orig instanceof ApolloError && isApolloSessionExpiry(orig)) {
        return null;
      }
      return event;
    },

    beforeBreadcrumb: excludeGraphQLFetch,
    // beforeBreadcrumb(breadcrumb) {
    //   // too noisy
    //   if (breadcrumb.category === 'redux.action') {
    //     return null;
    //   }

    //   return breadcrumb;
    // },

    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for performance monitoring.
    tracesSampleRate: 1.0,

    // Capture Replay for 10% of all sessions,
    // plus for 100% of sessions with an error
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  });
  Sentry.setTag('git_sha', gitSha);

  const accessToken = getCache('access_token') as TokenMeta | undefined;
  const token = accessToken?.token;
  let graphql = new GraphQLClient(config.apiUrl, token ? bearerAuth(token) : undefined);

  let viewer: Viewer = { ...store.getState().viewer };

  if (token) {
    try {
      viewer = await fetchViewer(graphql.client);
    } catch (err) {
      logout();
      if (!isUnauthorizedError(err)) {
        Sentry.captureException(err);
      }
      graphql = new GraphQLClient(config.apiUrl);
    }
  }

  // Set rate display cookie from viewer's billing account
  if (viewer.id) {
    rateDisplay.setFromBillingAccount(viewer.groups?.[0]?.billing_account);
  }

  // KT: setup sentry user as soon as possible to give more context
  if (viewer.id) {
    const segment = viewer.admin ? 'admin' : viewer.signup_type;
    const scope = Sentry.getCurrentScope();
    scope.setUser({
      id: viewer.id,
      username: viewer.username || undefined,
      email: viewer.email?.address || '',
      segment,
    });
  }

  viewer.intercomHash = getCache('intercom_hash');

  // ENG-5251: lazy accessors so mid-session signup/login/logout updates
  // (via authSession.authenticate) propagate without re-bootstrapping
  // these singletons. Previously both captured token/viewer at boot time,
  // which left a freshly-signed-up user with stale credentials until they
  // hard-refreshed (messaging "unavailable", websocket auth failure).
  const apiWebSocket = new ApiWebSocket(
    config.webSocketUrl,
    () => (store.getState().runtime.token as string | undefined) ?? undefined
  );
  const sendbird = new SendbirdHelper(() => store.getState().viewer, graphql);

  setThunkExtraArg({
    graphql,
    history,
    sendbird,
    voipCarrier,
    apiWebSocket,
  });
  store.dispatch(setViewer(viewer));
  store.dispatch(updateUserInfo(viewer));
  store.dispatch(setRuntimeVariable('referrer', document.referrer));
  store.dispatch(setRuntimeVariable('token', token));
  store.dispatch(setRuntimeVariable('login', accessToken?.login_meta || undefined));

  const cache = createEmotionCache();

  const {
    ui: { userContext },
  } = store.getState();
  await store.dispatch(setUserContext(getDefaultUserContext(viewer, userContext)));

  store.dispatch({ type: ActionTypes.LOAD_FROM_LOCAL_STORAGE });

  const permission = new PermissionService(graphql.client, viewer.id);
  // Register the boot-time instance so authSession.authenticate can
  // rebind viewer.id after a mid-session login (e.g., self-service
  // signup). The router middleware context captures this reference at
  // boot and would otherwise carry the empty-string userId forever.
  registerPermissionService(permission);

  // Global (context) variables that can be easily accessed from any React
  // component https://facebook.github.io/react/docs/context.html
  const context = {
    viewer,

    // Integrate Redux
    // http://redux.js.org/docs/basics/UsageWithReact.html
    store,

    // GraphQL client (Lokka)
    // https://github.com/kadirahq/lokka
    graphql,

    location: window.location,
    sendbird,
    voipCarrier,
    permission,
    apiWebSocket,
    capabilities: {
      screen: {
        width: window.screen.width,
      },
    },
  };

  // Telephony
  // voipCarrier.registerCarrier('twilio', TwilioCarrier);
  // voipCarrier.registerCarrier('plivo', PlivoCarrier);
  voipCarrier.registerCarrier('zoom', ZoomCarrier);
  voipCarrier.setTokenFactory((identifier: string) => store.dispatch(createToken(identifier)));
  voipCarrier.on('callStateChange', (state: object) => store.dispatch(updateCall(state)));
  voipCarrier.on('error', (err: VoipError) => {
    store.dispatch(notify(err.message, 'error'));
    trackConsultationJoin(store, 'error');
  });
  voipCarrier.on('connect', () => {
    trackConsultationJoin(store, 'success');
  });

  // Init

  initSegmentIdentifier(viewer);

  updateTracking();

  saveInvitationToken();

  saveTimezone(store, viewer);

  if (config.intercomAppId) {
    initIntercom(config.intercomAppId, viewer);
  }

  initHubSpot(viewer);

  // KT: some possibly relevant server code
  // const icons = {
  //   shortcut: `${cdnUrl}/images/icons/favicon.png`,
  //   appleTouch: `${cdnUrl}/images/icons/apple-touch-icon.png`,
  //   manifest: `${cdnUrl}/images/icons/site.webmanifest`,
  //   msAppConfig: `${cdnUrl}/images/icons/browserconfig.xml`,
  // };

  // const data = {
  //   title: 'OnFrontiers - Learn faster',
  //   meta: {
  //     description:
  //       'Automation for the knowledge economy | Access specialists on any topic within hours. Share your experience to help others on their path forward. Invest in your expert network.',
  //     url: location.toString(),
  //     imageUrl: '/images/meta.png',
  //     type: 'website',
  //   },

  // Route loaders read the EPA kill switch synchronously (ENG-5601) and run
  // before the first render, so the flags have to be in hand by here. Without
  // this the switch would only take effect from the second page load of a
  // session, which is exactly no use during the incident it exists for.
  //
  // The 1500ms ceiling means a slow or unreachable GrowthBook delays boot at
  // worst and never blocks it. On timeout every flag reads false, so the gate
  // runs — the safe answer.
  await growthBookReady(1500);

  // A tag rather than an event per boot. This is only ever true during an
  // incident affecting ~55k experts, and the Sentry init sets no `sampleRate`,
  // so capturing a message here would put one event per session boot into the
  // very stream being read to diagnose that incident, competing for the same
  // quota. As a tag it rides along on the errors already being reported and
  // costs nothing. Set in both directions so it can be filtered on either.
  Sentry.setTag('epa_gate_disabled', isFeatureOn(EPA_GATE_KILL_SWITCH));

  const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouter(createBrowserRouter);
  const router = sentryCreateBrowserRouter(
    createRoutes({ store, graphqlClient: graphql, sendbird, permission })
  );

  // Note that React strict mode doesn't work with @mui/styles
  // See https://mui.com/system/styles/basics/
  createRoot(document.getElementById('root') as Element).render(
    <ReduxProvider store={store}>
      <ThemeModeProvider>
        <CacheProvider value={cache}>
          <StyledEngineProvider injectFirst>
            <MuiThemeBridge>
              <Sentry.ErrorBoundary fallback={ErrorFallback}>
                <ApolloProvider client={graphql.client}>
                  <AppProvider context={context}>
                    <App>
                      <RouterProvider router={router} />
                    </App>
                  </AppProvider>
                </ApolloProvider>
              </Sentry.ErrorBoundary>
            </MuiThemeBridge>
          </StyledEngineProvider>
        </CacheProvider>
      </ThemeModeProvider>
    </ReduxProvider>
  );

  // If you want to start measuring performance in your app, pass a function
  // to log results (for example: reportWebVitals(console.log))
  // or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
  reportWebVitals();
})();
